import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { auth } from '../../firebase/config'
import Modal from '../common/Modal'
import TwoFactorSettings from './TwoFactorSettings'
import isPublicPath from '../../helpers/isPublicPath'
import { needsMfaSetup } from '../../helpers/auth/totp'

// Verplicht-scherm voor tweestapsverificatie (MFA fase 5). Wie de claim
// mfa_required heeft (organisatie verplicht het, of Deccos-superadmin) en niet
// met een tweede stap is ingelogd, krijgt van de regels en de Cloud Functions
// niets. Zonder dit scherm zou de app leeg lijken; nu ziet hij alleen
// 'Stel tweestapsverificatie in'. Zelfde blokkerende opzet als ConsentGate.
//
// Na het koppelen is de huidige sessie nog zonder tweede stap: pas opnieuw
// inloggen mét code geeft een token waarmee de regels doorlaten.
//
// Na koppelen of uitloggen laadt de pagina VOLLEDIG opnieuw, geen history.push.
// De rest van de app (Auth-context, lijsten) had al geladen met de geweigerde
// sessie en probeert het niet vanzelf opnieuw: de spinner bleef hangen tot een
// handmatige herlaadactie (productie 2026-11-02). En `client` (hooks/Client.js)
// wordt bij het laden van de module uit de url gelezen — zoals in Login.js is
// het herladen dragend.
// AI-CONTEXT: See docs/features/mfa-totp.md (fase 5)

/**
 * Geen props: kijkt naar het token van de ingelogde gebruiker.
 */
const MfaGate = () => {
    const location = useLocation()
    const [needed, setNeeded] = useState(false)

    useEffect(() => {
        // onIdTokenChanged en niet onAuthStateChanged: de claim komt mee bij elk
        // vers token, ook zonder opnieuw inloggen.
        const unsub = auth.onIdTokenChanged(async (user) => {
            if (!user) {
                setNeeded(false)
                return
            }
            const result = await user.getIdTokenResult()
            setNeeded(needsMfaSetup(result.claims))
        })
        return () => unsub()
    }, [])

    const toLogin = async () => {
        await auth.signOut()
        window.location.assign('/login')
    }

    const afterEnrolled = async () => {
        const result = await auth.currentUser.getIdTokenResult(true)
        if (needsMfaSetup(result.claims)) {
            await toLogin()
            return
        }
        window.location.reload()
    }

    // Publieke pagina's (vragenlijsten, de verificatielink) blijven bruikbaar —
    // behalve het inlogscherm: daar komt wie verplicht is maar nog geen app heeft.
    const onPublicPage = isPublicPath(location.pathname) && location.pathname !== '/login'
    if (!needed || onPublicPage) return null

    const content = (
        <div className='mfa-gate'>
            <p>Je organisatie vraagt om tweestapsverificatie. Koppel een authenticator-app; daarna log je opnieuw in met de code uit die app.</p>
            <TwoFactorSettings onEnrolled={afterEnrolled} />
            <div className='two-factor-actions'>
                <button type='button' onClick={toLogin}>Uitloggen</button>
            </div>
        </div>
    )

    return (
        <Modal
            modalOpen
            setModalOpen={() => {}} // nooit aangeroepen: `blocking` sluit elke uitweg af
            pageHeader='Stel tweestapsverificatie in'
            content={content}
            blocking
            actions={null}
        />
    )
}

export default MfaGate
